import type { EvergreenSlugEntry } from './i18n';
import type { TopPage } from './related-pages';
import type { ReactNode } from 'react';

/**
 * Evergreen page renderer registry.
 *
 * Each page type module (country-month, best-time, phenomenon, festival)
 * registers a render function that receives the slug entry and returns
 * a React element. This keeps the [slug]/page.tsx resolver simple while
 * allowing each page type to own its rendering.
 */

type EvergreenRenderer = (
  entry: EvergreenSlugEntry,
  topPages: TopPage[],
) => ReactNode;

const _renderers: Record<string, EvergreenRenderer> = {};

/** Register a renderer for a page type. Called by page type modules. */
export function registerEvergreenRenderer(pageType: string, renderer: EvergreenRenderer) {
  _renderers[pageType] = renderer;
}

/** Render an evergreen page by delegating to the registered renderer. */
export function renderEvergreenPage(
  entry: EvergreenSlugEntry,
  topPages: TopPage[],
): ReactNode {
  const renderer = _renderers[entry.pageType];
  if (!renderer) return null;
  return renderer(entry, topPages);
}
